<template>
  <div class="searchableSelectWrapper">
  <div
    :class="[
      'selectPlus',
      'searchableSelect',
      { searchableSelectWithCreate: enableIconCreate && openCreateModal },
      { searchableSelectWithFilters: additionalFilters.length > 0 },
    ]"
  >
    <label
      :style="`background-color: ${labelBackground}`"
      :class="[
        'fixedSelect',
        { fixedSelectNotDisabled: label && isDisabled },
        { invalidLabel: isInvalidInput },
        { fixedSelectDisable: !label },
      ]"
    >
      {{ label }}
    </label>

    <v-select
      v-model="valueItems"
      class="searchableSelectInput"
      :class="{ isInvalidInput, inputDisabled: isDisabled }"
      :options="filteredItems"
      :filter="additionalFilters.length ? filterTextOptions : undefined"
      :label="textField"
      :reduce="getOptionValue"
      :disabled="isDisabled"
      :clearable="true"
      :tabindex="tabindex"
      @input="selectValue"
      @blur="clearInvalidInput"
    >
      <template #no-options>
        Resultado não encontrado!
      </template>
    </v-select>

    <button
      v-if="enableIconCreate && openCreateModal"
      type="button"
      :class="['iconSelect', { iconDisabled: isDisabled }]"
      :disabled="isDisabled"
      :aria-label="`Cadastrar ${label}`"
      @click="createModal"
    >
      <b-icon-plus scale="2" />
    </button>
    <button
      v-if="additionalFilters.length"
      ref="filterTrigger"
      type="button"
      class="selectFilterButton"
      :disabled="isDisabled"
      :aria-expanded="filterOpen"
      aria-label="Filtrar opções"
      @click="toggleFilter"
    >
      <b-icon-filter scale="1.3" />
    </button>
    <div
      v-if="additionalFilters.length"
      v-show="filterOpen"
      ref="filterPanel"
      class="selectFilterPanel"
    >
      <div class="selectFilterTextMode">
        <label><input v-model="filterType" type="radio" value="includes" /> Contém</label>
        <label><input v-model="filterType" type="radio" value="start" /> Iniciar por</label>
      </div>
      <button type="button" class="selectOpenAdditionalFilters" @click="openAdditionalFilters">Filtros adicionais</button>
    </div>
  </div>
  <AdditionalFiltersModal
    v-if="additionalFilters.length"
    ref="additionalFiltersModal"
    :fields="additionalFilters"
    :appliedFilters="appliedFilters"
    @opening="$emit('openFilters')"
    @apply="applyAdditionalFilters"
  />
  </div>
</template>

<script>
import AdditionalFiltersModal from "./AdditionalFiltersModal.vue";

export default {
  name: "InputSearchSelect",
  components: { AdditionalFiltersModal },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    valueField: {
      type: String,
      required: true,
    },
    textField: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: "",
    },
    dataSelect: {
      type: [String, Number, Boolean],
      default: "",
    },
    openCreateModal: {
      type: Function,
    },
    enableIconCreate: {
      type: Boolean,
      default: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    labelBackground: {
      type: String,
      default: "#fff",
    },
    isInvalidInput: {
      type: String,
      default: "",
    },
    tabindex: {
      type: [String, Number],
    },
    additionalFilters: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      valueItems: this.dataSelect,
      filterOpen: false,
      filterType: "includes",
      appliedFilters: {},
      filterListenersAttached: false,
    };
  },
  computed: {
    filteredItems() {
      if (!this.additionalFilters.length || !Object.keys(this.appliedFilters).length) return this.items;
      const filtered = this.items.filter((item) => this.additionalFilters.every((field) => {
        const expected = this.appliedFilters[field.key];
        if (expected === undefined) return true;
        const actual = field.getValue?.(item);
        return String(actual ?? "") === String(expected);
      }));
      // Keep the selected option available so vue-select can display its label.
      const selected = this.items.find((item) => item[this.valueField] === this.valueItems);
      if (selected && !filtered.includes(selected)) filtered.push(selected);
      return filtered;
    },
  },
  mounted() {
    this.syncFilterListeners();
  },
  beforeDestroy() {
    if (this.filterListenersAttached) {
      document.removeEventListener("click", this.closeFilterOutside);
    }
  },
  methods: {
    syncFilterListeners() {
      const enabled = this.additionalFilters.length > 0;
      if (enabled === this.filterListenersAttached) return;
      if (enabled) {
        document.addEventListener("click", this.closeFilterOutside);
      } else {
        document.removeEventListener("click", this.closeFilterOutside);
      }
      this.filterListenersAttached = enabled;
    },
    filterTextOptions(options, search) {
      const text = search.toLowerCase();
      return options.filter((item) => [item[this.valueField], item[this.textField]].some((field) => {
        const value = String(field ?? "").toLowerCase();
        return this.filterType === "start" ? value.startsWith(text) : value.includes(text);
      }));
    },
    toggleFilter() {
      this.filterOpen = !this.filterOpen;
    },
    openAdditionalFilters() {
      this.filterOpen = false;
      this.$refs.additionalFiltersModal.open();
    },
    applyAdditionalFilters(filters) {
      this.appliedFilters = filters;
    },
    closeFilterOutside(event) {
      if (this.filterOpen && !this.$refs.filterPanel?.contains(event.target) && !this.$refs.filterTrigger?.contains(event.target)) {
        this.filterOpen = false;
      }
    },
    getOptionValue(item) {
      return item[this.valueField];
    },
    selectValue(value) {
      const normalizedValue = value ?? "";
      this.valueItems = normalizedValue;
      this.$emit("update:dataSelect", normalizedValue);
      this.$emit("selectedIten", normalizedValue);

      const selectedCompletedItem = this.items.find(
        (item) => item[this.valueField] === normalizedValue
      );
      this.$emit("selectedCompletedItem", selectedCompletedItem);
      this.$emit("changed");
    },
    createModal() {
      this.openCreateModal();
    },
    clearInvalidInput() {
      this.$emit("clearInvalidInput");
    },
  },
  watch: {
    additionalFilters(fields) {
      this.syncFilterListeners();
      if (!fields.length) this.filterOpen = false;
    },
    dataSelect(newValue) {
      if (newValue !== this.valueItems) {
        this.valueItems = newValue ?? "";
      }
    },
  },
};
</script>

<style scoped>
.searchableSelectWrapper {
  width: 100%;
  min-width: 0;
}

.searchableSelect {
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
}

.searchableSelectInput {
  width: 100%;
  min-width: 0;
}

.searchableSelectWithCreate .searchableSelectInput {
  width: calc(100% - 20px);
}

.searchableSelectWithFilters .searchableSelectInput {
  width: calc(100% - 20px);
}

.searchableSelectWithCreate.searchableSelectWithFilters .searchableSelectInput {
  width: calc(100% - 40px);
}

.selectFilterButton {
  display: flex;
  width: 20px;
  height: 30px;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 0 0.25rem 0.25rem 0;
  background: #006256;
  color: white;
  cursor: pointer;
}

.searchableSelectWithCreate.searchableSelectWithFilters .iconSelect {
  border-radius: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.28);
}

.searchableSelectWithCreate.searchableSelectWithFilters .selectFilterButton {
  border-radius: 0;
}

.selectFilterPanel {
  position: absolute;
  top: 34px;
  right: 0;
  z-index: 500;
  display: flex;
  width: max-content;
  height: auto;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
  border-radius: 5px;
  background: white;
  box-shadow: 0 0 4px #0004;
}

.selectFilterTextMode {
  display: flex;
  gap: 10px;
  font-size: 11px;
}

.selectFilterTextMode label {
  margin: 0;
}

.selectOpenAdditionalFilters {
  padding: 5px 8px;
  border: 0;
  border-radius: 4px;
  background: #006256;
  color: white;
  font-size: 11px;
  cursor: pointer;
}

.fixedSelect {
  position: absolute;
  left: 3px;
  bottom: 13px;
  z-index: 10;
  height: 12px;
  padding: 0 0.2em;
  color: #666360;
  font-family: Poppins-Regular, sans-serif !important;
  font-size: 11px;
  font-weight: 600;
  pointer-events: none;
  transform: translateY(-50%) scale(0.8);
  white-space: nowrap;
}
.selectPlus{
  gap: 0px !important;
}
.fixedSelectDisable,
.fixedSelectNotDisabled {
  padding: 0;
}

.invalidLabel {
  color: rgba(142, 15, 15, 0.583);
}

.iconSelect {
  display: flex;
  width: 20px;
  height: 30px;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 0 0.25rem 0.25rem 0;
  background-color: #006256;
  color: whitesmoke;
  cursor: pointer;
}

.iconDisabled,
.inputDisabled {
  pointer-events: none;
  opacity: 0.7;
}

.isInvalidInput {
  box-shadow: 0 0 0 1.5px rgba(204, 21, 21, 0.583);
}

.searchableSelectInput ::v-deep .vs__dropdown-toggle {
  height: 30px;
  min-height: 30px;
  padding: 0 0 4px;
  border: 1px solid #d5d3d3;
  border-radius: 0.25rem;
  color: #5c5855;
}

.searchableSelectWithCreate
  .searchableSelectInput
  ::v-deep .vs__dropdown-toggle {
  border-radius: 0.25rem 0 0 0.25rem;
}

.searchableSelectInput ::v-deep .vs__selected,
.searchableSelectInput ::v-deep .vs__search {
  margin-top: 2px;
  font-size: 12px;
}

.searchableSelectInput ::v-deep .vs__dropdown-menu {
  z-index: 1000;
  overflow-x: hidden;
  font-size: 12px;
}

.searchableSelectInput ::v-deep .vs__dropdown-option {
  overflow: hidden;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: normal;
}

@media (max-width: 768px) {
  .fixedSelect {
    left: 0;
    bottom: 14px;
    font-size: 14px;
  }
}
</style>
